import { Dialog, DialogBackdrop, DialogPanel, DialogTitle } from '@headlessui/react';
import { useState, useEffect, useMemo } from 'react';
import { collection, query, where, orderBy, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../AuthContext';
import { toast } from 'react-hot-toast';

export const OpenDocumentsDialog = ({ isOpen, onClose, onSelectDocument, currentDocumentId }) => {
  const { user } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if (!isOpen || !user) return;

    setSearchQuery(''); // Reset search when opening
    setLoading(true);

    // Set up real-time listener for documents
    const q = query(
      collection(db, 'documents'),
      where('userId', '==', user.uid),
      orderBy('updatedAt', 'desc')
    );

    const unsubscribe = onSnapshot(
      q,
      (snapshot) => {
        const docs = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setDocuments(docs);
        setLoading(false);
      },
      (error) => {
        console.error('Error loading documents:', error);
        toast.error('Failed to load notes');
        setLoading(false);
      }
    );

    // Cleanup listener when dialog closes or component unmounts
    return () => unsubscribe();
  }, [isOpen, user]);

  // Filter documents based on search query (searches title, block titles, and element names/values)
  const filteredDocuments = useMemo(() => {
    if (!searchQuery.trim()) return documents;

    const query = searchQuery.toLowerCase();
    return documents.filter(doc => {
      // Search in title
      if (doc.title?.toLowerCase().includes(query)) return true;

      // Search in blocks
      if (doc.blocks) {
        for (const block of doc.blocks) {
          // Search block title
          if (block.title?.toLowerCase().includes(query)) return true;

          // Search elements
          if (block.elements) {
            for (const el of block.elements) {
              if (el.name?.toLowerCase().includes(query)) return true;
              if (el.value?.toString().toLowerCase().includes(query)) return true;
            }
          }
        }
      }

      return false;
    });
  }, [documents, searchQuery]);

  const handleSelect = (document) => {
    onSelectDocument(document);
    onClose();
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <Dialog open={isOpen} onClose={onClose} className="relative z-50 text-white">
      <DialogBackdrop className="fixed inset-0 bg-black/70" />
      <div className="fixed inset-0 flex w-screen items-center justify-center">
        <DialogPanel className="border border-white/20 w-full max-w-[600px] h-full sm:max-h-[80vh] rounded-[30px] bg-[#4A4A4A]/10 backdrop-blur-sm p-6 flex flex-col">
          <DialogTitle className="font-bold text-xl mb-4 flex items-center gap-3">
            My Lists
          </DialogTitle>

          {/* Search input */}
          <div className="relative mb-4">
            <svg
              className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Search..."
              className="w-full bg-transparent border-b border-white/10 py-2 pl-10 pr-4 text-white placeholder-gray-500 outline-none focus:border-white/30 transition-colors"
              autoFocus
            />
          </div>

          <div className="flex-1 overflow-y-auto min-h-0">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-white"></div>
              </div>
            ) : documents.length === 0 ? (
              <div className="text-center py-12 text-gray-400">
                <p>No notes yet</p>
                <p className="text-sm mt-2">Create a new note or use a template to get started</p>
              </div>
            ) : filteredDocuments.length === 0 ? (
              <div className="text-center py-12 text-gray-400">
                <p>No notes match "{searchQuery}"</p>
              </div>
            ) : (
              <div className="space-y-1">
                {filteredDocuments.map((doc) => {
                  const isCurrentDoc = doc.id === currentDocumentId;
                  return (
                    <button
                      key={doc.id}
                      onClick={() => handleSelect(doc)}
                      className={`w-full text-left px-4 py-2 border-l-1 transition-all group flex items-center justify-between ${isCurrentDoc
                          ? 'border-l-[#7BCDBA]/80'
                          : 'border-l-white/10 hover:border-l-white/50'
                        }`}
                    >
                      <div className="min-w-0 flex-1 flex items-center gap-2">
                        {isCurrentDoc && (
                          <div className="w-2 h-2 rounded-full bg-[#7BCDBA] shrink-0" title="Currently open" />
                        )}
                        <div className="min-w-0 flex-1">
                          <h3 className={`font-semibold truncate ${isCurrentDoc ? 'text-[#7BCDBA]' : 'text-white'}`}>
                            {doc.title || 'Heading'}
                          </h3>
                          <p className="text-sm text-gray-400">
                            {formatDate(doc.updatedAt)}
                          </p>
                        </div>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          <button
            onClick={onClose}
            className="mt-6 w-full py-2 text-gray-400 hover:text-white transition-colors text-sm"
          >
            Cancel
          </button>
        </DialogPanel>
      </div>
    </Dialog>
  );
};
